import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getData, postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";
import { useGuidance } from "./T2TGuidanceProvider";

const WIZARD_STEPS = [
  { id: 1, name: "name_and_purpose",  title: "What are you building?" },
  { id: 2, name: "first_stage",       title: "Who starts the process?" },
  { id: 3, name: "connect_stages",    title: "Who reviews and approves?" },
  { id: 4, name: "assign_roles",      title: "Who plays each role?" },
  { id: 5, name: "conditions",        title: "Any special routing rules?" },
  { id: 6, name: "review_publish",    title: "Ready to go live?" },
];

const DEFAULT_FIELDS = [
  { id: "requester_name", label: "Requester Name", type: "text",     auto: true,  checked: true },
  { id: "request_date",   label: "Request Date",   type: "date",     auto: true,  checked: true },
  { id: "amount",         label: "Amount / Value",  type: "currency", auto: false, checked: false },
  { id: "description",    label: "Description",     type: "textarea", auto: false, checked: false },
  { id: "documents",      label: "Supporting Documents", type: "file", auto: false, checked: false },
];

const OPERATORS = [
  { value: "gt",  label: "is more than" },
  { value: "lt",  label: "is less than" },
  { value: "eq",  label: "equals" },
  { value: "neq", label: "does not equal" },
  { value: "gte", label: "is at least" },
  { value: "lte", label: "is at most" },
];

const T2TGuidedWizard = ({ open, onClose, onComplete }) => {
  const toast = useToast();
  const { advanceWizard, completeWizard, abandonWizard, state: guidanceState } = useGuidance();

  // Wizard state
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const [createdStageIds, setCreatedStageIds] = useState([]);
  const [createdTransitionIds, setCreatedTransitionIds] = useState([]);

  // Step 1 — Name & Purpose
  const [wfName, setWfName] = useState("");
  const [wfDesc, setWfDesc] = useState("");

  // Step 2 — First Stage
  const [firstStageName, setFirstStageName] = useState("Request Submission");
  const [formFields, setFormFields] = useState(DEFAULT_FIELDS.map((f) => ({ ...f })));
  const [customField, setCustomField] = useState({ name: "", type: "text" });
  const [showAddField, setShowAddField] = useState(false);

  // Step 3 — Approval Steps
  const [approvalSteps, setApprovalSteps] = useState([
    { id: 1, name: "", action: "both" },
  ]);

  // Step 4 — Role Assignment
  const [availableRoles, setAvailableRoles] = useState([]);
  const [stageRoles, setStageRoles] = useState({}); // { stageIndex: roleId }
  const [showCreateRole, setShowCreateRole] = useState(null); // index of stage needing new role
  const [newRoleName, setNewRoleName] = useState("");

  // Step 5 — Conditions
  const [hasConditions, setHasConditions] = useState(false);
  const [conditions, setConditions] = useState([]);

  // Step 6 — built from all above

  // Resume wizard if in progress
  useEffect(() => {
    if (open && guidanceState.wizard_current_step > 1 && guidanceState.wizard_workflow_id) {
      setStep(guidanceState.wizard_current_step);
      setWorkflowId(guidanceState.wizard_workflow_id);
    }
  }, [open, guidanceState.wizard_current_step, guidanceState.wizard_workflow_id]);

  // Fetch roles for Step 4
  useEffect(() => {
    if (open) {
      getData("t2t/roles").then((resp) => {
        if (!resp.error) {
          const raw = resp.data?.data;
          setAvailableRoles(Array.isArray(raw) ? raw : []);
        }
      });
    }
  }, [open]);

  if (!open) return null;

  const allStages = [
    { name: firstStageName, type: "approval" },
    ...approvalSteps.map((s) => ({ name: s.name, type: "approval" })),
    { name: "End", type: "end" },
  ];

  // ---- Step handlers — all use POST /t2t/guidance/wizard/step ----

  const sendStep = async (stepNum, stepData) => {
    const resp = await postData("t2t/guidance/wizard/step", { step: stepNum, step_data: stepData });
    if (resp.error) { toast(resp.error.message || `Step ${stepNum} failed.`, "error"); return false; }
    // Step responses may include created stage IDs — store for later steps
    const respData = resp.data?.data;
    if (respData?.stage_ids) setCreatedStageIds(respData.stage_ids);
    if (respData?.transition_ids) setCreatedTransitionIds(respData.transition_ids);
    return true;
  };

  const handleStep1Next = async () => {
    if (!wfName.trim() || wfName.trim().length < 3) { toast("Workflow name must be at least 3 characters.", "warning"); return; }
    setSaving(true);

    if (!workflowId) {
      const resp = await postData("t2t/guidance/wizard/start", { name: wfName.trim(), description: wfDesc.trim() });
      if (resp.error) { toast(resp.error.message || "Failed to create workflow.", "error"); setSaving(false); return; }
      const newId = resp.data?.data?.wizard_workflow_id || resp.data?.data?.workflow_id;
      setWorkflowId(newId);
    }

    const ok = await sendStep(1, { name: wfName.trim(), description: wfDesc.trim() });
    if (ok) { setStep(2); advanceWizard(2); }
    setSaving(false);
  };

  const handleStep2Next = async () => {
    if (!firstStageName.trim()) { toast("Stage name is required.", "warning"); return; }
    setSaving(true);
    const selectedFields = formFields.filter((f) => f.checked);
    const ok = await sendStep(2, {
      stage_name: firstStageName.trim(),
      form_fields: selectedFields.map((f) => ({ label: f.label, field_type: f.type, is_required: f.auto })),
    });
    if (ok) { setStep(3); advanceWizard(3); }
    setSaving(false);
  };

  const handleStep3Next = async () => {
    const validSteps = approvalSteps.filter((s) => s.name.trim());
    if (validSteps.length === 0) { toast("Add at least one approval step.", "warning"); return; }
    setSaving(true);
    const ok = await sendStep(3, {
      stages: validSteps.map((s) => ({ name: s.name.trim() })),
    });
    if (ok) { setStep(4); advanceWizard(4); }
    setSaving(false);
  };

  const handleStep4Next = async () => {
    const allAssigned = allStages.slice(0, -1).every((_, i) => stageRoles[i]);
    if (!allAssigned) { toast("Please assign a role to every stage.", "warning"); return; }
    setSaving(true);

    // Build role_assignments using created stage IDs if available
    const assignments = Object.entries(stageRoles).map(([idx, roleId]) => ({
      stage_id: createdStageIds[Number(idx)] || Number(idx),
      role_ids: [Number(roleId)],
    }));

    const ok = await sendStep(4, { role_assignments: assignments });
    if (ok) { setStep(5); advanceWizard(5); }
    setSaving(false);
  };

  const handleStep5Next = async () => {
    setSaving(true);
    const condData = hasConditions
      ? conditions.filter((c) => c.field && c.operator && c.value).map((c) => ({
          transition_id: createdTransitionIds[Number(c.thenStage)] || null,
          rules: [{ field_id: c.field, operator: c.operator, value: c.value }],
          logic: "and",
        }))
      : [];
    const ok = await sendStep(5, { conditions: condData });
    if (ok) { setStep(6); advanceWizard(6); }
    setSaving(false);
  };

  const handlePublish = async () => {
    setSaving(true);
    await sendStep(6, {});
    const resp = await postData("t2t/guidance/wizard/complete", { publish: true });
    if (resp.error) { toast(resp.error.message || "Failed to publish.", "error"); setSaving(false); return; }
    await completeWizard();
    toast("Workflow published! 🎉", "success");
    setSaving(false);
    if (onComplete) onComplete(workflowId);
  };

  const handleSaveDraft = async () => {
    await sendStep(6, {});
    await postData("t2t/guidance/wizard/complete", { publish: false });
    toast("Workflow saved as draft. You can publish it later from the canvas.", "success");
    if (onComplete) onComplete(workflowId);
  };

  const handleExit = () => {
    abandonWizard(step);
    onClose();
  };

  const addApprovalStep = () => {
    if (approvalSteps.length >= 8) { toast("Maximum 8 approval steps in the wizard.", "warning"); return; }
    setApprovalSteps((prev) => [...prev, { id: Date.now(), name: "", action: "both" }]);
  };

  const addCustomField = () => {
    if (!customField.name.trim()) return;
    setFormFields((prev) => [...prev, { id: `custom-${Date.now()}`, label: customField.name.trim(), type: customField.type, auto: false, checked: true }]);
    setCustomField({ name: "", type: "text" });
    setShowAddField(false);
  };

  const createRoleInline = async (stageIdx) => {
    if (!newRoleName.trim()) return;
    setSaving(true);
    const resp = await postData("t2t/roles", { name: newRoleName.trim(), color: "#36B82A" });
    if (!resp.error) {
      const newRole = resp.data?.data;
      const roleId = newRole?.role_id || newRole?.id;
      setAvailableRoles((prev) => [...prev, { role_id: roleId, name: newRoleName.trim(), color: "#36B82A" }]);
      setStageRoles((prev) => ({ ...prev, [stageIdx]: roleId }));
      toast(`Role "${newRoleName}" created.`, "success");
    } else {
      toast(resp.error.message || "Failed to create role.", "error");
    }
    setNewRoleName("");
    setShowCreateRole(null);
    setSaving(false);
  };

  const addCondition = () => {
    setConditions((prev) => [...prev, { id: Date.now(), field: "", operator: "gt", value: "", thenStage: "" }]);
  };

  const progressPercent = Math.round((step / 6) * 100);

  return (
    <div className="t2t-welcome-overlay">
      <div className="t2t-wizard-modal">
        {/* Header */}
        <div className="t2t-wizard-header">
          <div>
            <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 2 }}>Step {step} of 6</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{WIZARD_STEPS[step - 1].title}</div>
          </div>
          <button className="t2t-close-btn" onClick={handleExit} title="Exit wizard">×</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: "#E2E8F0" }}>
          <div style={{ height: "100%", width: `${progressPercent}%`, background: "var(--t2t-primary)", transition: "width 0.3s ease" }} />
        </div>

        {/* Body */}
        <div className="t2t-wizard-body">

          {/* ============ STEP 1 — Name & Purpose ============ */}
          {step === 1 && (
            <>
              <div className="t2t-fg">
                <label>Give it a name: *</label>
                <input className="t2t-fc" value={wfName} onChange={(e) => setWfName(e.target.value)} placeholder="e.g. Vendor Approval, Purchase Request, Leave Application" autoFocus />
              </div>
              <div className="t2t-fg">
                <label>Describe what this workflow is for <span style={{ fontWeight: 400, color: "var(--t2t-text-muted)" }}>(optional)</span></label>
                <textarea className="t2t-fc" rows={3} value={wfDesc} onChange={(e) => setWfDesc(e.target.value)}
                  placeholder="e.g. When our team wants to register a new supplier, they fill in this form and it gets approved by the finance team." />
              </div>
            </>
          )}

          {/* ============ STEP 2 — First Stage ============ */}
          {step === 2 && (
            <>
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>Every workflow starts with someone submitting a request.</p>
              <div className="t2t-fg">
                <label>Stage name:</label>
                <input className="t2t-fc" value={firstStageName} onChange={(e) => setFirstStageName(e.target.value)} placeholder="e.g. Request Submission" />
                <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginTop: 2 }}>You can change this later.</div>
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: "block" }}>What information do they fill in?</label>
                {formFields.map((f) => (
                  <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", cursor: "pointer", fontSize: 13 }}>
                    <input type="checkbox" checked={f.checked} disabled={f.auto}
                      onChange={() => setFormFields((prev) => prev.map((ff) => ff.id === f.id ? { ...ff, checked: !ff.checked } : ff))}
                      style={{ width: 16, height: 16 }} />
                    {f.label}
                    {f.auto && <span style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>(auto-filled)</span>}
                  </label>
                ))}
                {showAddField ? (
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <input className="t2t-fc" style={{ flex: 2 }} value={customField.name} onChange={(e) => setCustomField((p) => ({ ...p, name: e.target.value }))} placeholder="Field name" autoFocus />
                    <select className="t2t-fc" style={{ flex: 1 }} value={customField.type} onChange={(e) => setCustomField((p) => ({ ...p, type: e.target.value }))}>
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="file">File Upload</option>
                      <option value="yes_no">Yes / No</option>
                      <option value="dropdown">List of options</option>
                    </select>
                    <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={addCustomField}>Add</button>
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setShowAddField(false)}>Cancel</button>
                  </div>
                ) : (
                  <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ marginTop: 8 }} onClick={() => setShowAddField(true)}>+ Add a custom field</button>
                )}
              </div>
            </>
          )}

          {/* ============ STEP 3 — Connect Stages ============ */}
          {step === 3 && (
            <>
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>Add as many approval steps as your process needs. They will be connected in order automatically.</p>
              {approvalSteps.map((s, i) => (
                <div key={s.id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 10, position: "relative" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--t2t-text-muted)" }}>Approval Step {i + 1}</span>
                    {approvalSteps.length > 1 && (
                      <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#DC2626" }}
                        onClick={() => setApprovalSteps((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </div>
                  <div className="t2t-fg" style={{ marginBottom: 8 }}>
                    <input className="t2t-fc" value={s.name} placeholder="e.g. Line Manager Approval, Finance Review"
                      onChange={(e) => setApprovalSteps((prev) => prev.map((ss, j) => j === i ? { ...ss, name: e.target.value } : ss))} />
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                    <span style={{ color: "var(--t2t-text-muted)" }}>What they can do:</span>
                    {[{ v: "approve", l: "Approve only" }, { v: "reject", l: "Reject only" }, { v: "both", l: "Both" }].map((opt) => (
                      <label key={opt.v} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                        <input type="radio" name={`action-${s.id}`} checked={s.action === opt.v}
                          onChange={() => setApprovalSteps((prev) => prev.map((ss, j) => j === i ? { ...ss, action: opt.v } : ss))} />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={addApprovalStep}>+ Add another approval step</button>
              <p style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 12 }}>
                Don&apos;t worry about the order — you can rearrange stages on the canvas after this.
              </p>
            </>
          )}

          {/* ============ STEP 4 — Assign Roles ============ */}
          {step === 4 && (
            <>
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>Who in your team handles each step?</p>
              {allStages.slice(0, -1).map((stage, i) => (
                <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{stage.name || `Stage ${i + 1}`}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select className="t2t-fc" style={{ flex: 1 }} value={stageRoles[i] || ""}
                      onChange={(e) => { setStageRoles((prev) => ({ ...prev, [i]: e.target.value })); setShowCreateRole(null); }}>
                      <option value="">Select a role...</option>
                      {availableRoles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
                    </select>
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 11, whiteSpace: "nowrap" }}
                      onClick={() => { setShowCreateRole(showCreateRole === i ? null : i); setNewRoleName(""); }}>
                      + Create role
                    </button>
                  </div>
                  {showCreateRole === i && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                      <input className="t2t-fc" style={{ flex: 1 }} value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)}
                        placeholder="New role name..." autoFocus onKeyDown={(e) => e.key === "Enter" && createRoleInline(i)} />
                      <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={() => createRoleInline(i)} disabled={saving}>Create</button>
                    </div>
                  )}
                </div>
              ))}
              <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 8, padding: "8px 12px", background: "#EFF6FF", borderRadius: 6 }}>
                💡 Roles control who can see and act on each step. You can assign multiple people to one role later.
              </div>
            </>
          )}

          {/* ============ STEP 5 — Conditions ============ */}
          {step === 5 && (
            <>
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>Does the approval path change based on any rules? For example: large purchases need extra approval.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                  <input type="radio" checked={!hasConditions} onChange={() => { setHasConditions(false); setConditions([]); }} />
                  No — everyone follows the same path <span style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>(most common)</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                  <input type="radio" checked={hasConditions} onChange={() => { setHasConditions(true); if (conditions.length === 0) addCondition(); }} />
                  Yes — it depends on certain values
                </label>
              </div>

              {hasConditions && (
                <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 16 }}>
                  {conditions.map((cond, ci) => {
                    const condFields = formFields.filter((f) => f.checked);
                    return (
                      <div key={cond.id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--t2t-text-muted)" }}>Routing Rule {ci + 1}</span>
                          {conditions.length > 1 && (
                            <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#DC2626" }}
                              onClick={() => setConditions((prev) => prev.filter((_, j) => j !== ci))}>✕</button>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: 13 }}>
                          <span>When</span>
                          <select className="t2t-fc" style={{ width: 130 }} value={cond.field}
                            onChange={(e) => setConditions((prev) => prev.map((c, j) => j === ci ? { ...c, field: e.target.value } : c))}>
                            <option value="">Select field...</option>
                            {condFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                          </select>
                          <select className="t2t-fc" style={{ width: 130 }} value={cond.operator}
                            onChange={(e) => setConditions((prev) => prev.map((c, j) => j === ci ? { ...c, operator: e.target.value } : c))}>
                            {OPERATORS.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          <input className="t2t-fc" style={{ width: 100 }} value={cond.value} placeholder="value"
                            onChange={(e) => setConditions((prev) => prev.map((c, j) => j === ci ? { ...c, value: e.target.value } : c))} />
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, marginTop: 8 }}>
                          <span>Then send to:</span>
                          <select className="t2t-fc" style={{ flex: 1 }} value={cond.thenStage}
                            onChange={(e) => setConditions((prev) => prev.map((c, j) => j === ci ? { ...c, thenStage: e.target.value } : c))}>
                            <option value="">Select stage...</option>
                            {allStages.slice(0, -1).map((s, si) => <option key={si} value={si}>{s.name || `Stage ${si + 1}`}</option>)}
                          </select>
                        </div>
                        {cond.field && cond.operator && cond.value && (
                          <div style={{ fontSize: 11, color: "#7C3AED", marginTop: 8, fontStyle: "italic", padding: "4px 8px", background: "#F5F3FF", borderRadius: 4 }}>
                            In plain English: &quot;If the {condFields.find((f) => f.id === cond.field)?.label || cond.field} {OPERATORS.find((o) => o.value === cond.operator)?.label || cond.operator} {cond.value}, route to {allStages[cond.thenStage]?.name || "..."}&quot;
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={addCondition}>+ Add another rule</button>
                </div>
              )}
            </>
          )}

          {/* ============ STEP 6 — Review & Publish ============ */}
          {step === 6 && (
            <>
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: 16, marginBottom: 20, textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{wfName}</div>
                <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginTop: 4 }}>
                  {allStages.length - 1} stages · {hasConditions ? `${conditions.length} routing rule${conditions.length !== 1 ? "s" : ""}` : "No routing rules"} · {Object.keys(stageRoles).length} roles assigned
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Here is what happens when someone submits a request:</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {allStages.map((s, i) => {
                    const role = i < allStages.length - 1 ? availableRoles.find((r) => String(r.role_id) === String(stageRoles[i])) : null;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "6px 10px", background: i === allStages.length - 1 ? "#1B3A6B" : "#F8FAFC", color: i === allStages.length - 1 ? "white" : "inherit", borderRadius: 6 }}>
                        <span style={{ fontWeight: 700, width: 20 }}>{i + 1}.</span>
                        <span style={{ flex: 1 }}>{s.name}</span>
                        {role && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 20, background: role.color || "#36B82A", color: "white", fontWeight: 600 }}>{role.name}</span>}
                        {s.type === "end" && <span style={{ fontSize: 10 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ background: "#EFF6FF", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>What publishing means:</div>
                <ul style={{ fontSize: 12, color: "var(--t2t-text-secondary)", margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                  <li>Your team can immediately start submitting requests</li>
                  <li>You can edit this workflow at any time</li>
                  <li>Every change creates a new version — nothing is lost</li>
                  <li>Requests already in progress are not affected by changes</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="t2t-wizard-footer">
          <div>
            {step > 1 && <button className="t2t-btn t2t-btn-ghost" onClick={() => setStep(step - 1)} disabled={saving}>← Back</button>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {step < 6 && (
              <button className="t2t-btn t2t-btn-primary" disabled={saving}
                onClick={step === 1 ? handleStep1Next : step === 2 ? handleStep2Next : step === 3 ? handleStep3Next : step === 4 ? handleStep4Next : handleStep5Next}>
                {saving ? "Saving..." : `Next: ${WIZARD_STEPS[step]?.title || "Continue"} →`}
              </button>
            )}
            {step === 6 && (
              <>
                <button className="t2t-btn t2t-btn-ghost" onClick={handleSaveDraft} disabled={saving}>Save as draft</button>
                <button className="t2t-btn t2t-btn-primary" onClick={handlePublish} disabled={saving}>
                  {saving ? "Publishing..." : "✅ Publish Now"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

T2TGuidedWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
};

export default T2TGuidedWizard;
